import React, { useState, useEffect } from 'react';
import {
  Card,
  Banner,
  Stack,
  TextStyle,
  ProgressBar,
  Button,
  Spinner,
  Badge
} from '@shopify/polaris';
import { useAuth } from '../contexts/AuthContext';
import { getUserCreditStatus, checkAndProcessRenewal } from '../services/creditService';

const UserCreditsInfo = () => {
  const { user } = useAuth();
  const [creditInfo, setCreditInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user) {
      fetchCreditInfo();
    }
  }, [user]);

  const fetchCreditInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if subscription needs renewal
      await checkAndProcessRenewal(user.uid);
      
      // Get updated credit status
      const info = await getUserCreditStatus(user.uid);
      console.log('Credit info:', info); // Debug log
      setCreditInfo(info);
    } catch (err) {
      console.error('Error fetching credit info:', err);
      setError('Failed to load credit information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card sectioned>
        <Stack distribution="center">
          <Spinner size="large" />
        </Stack>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sectioned>
        <Banner status="critical">
          <p>{error}</p>
        </Banner>
      </Card>
    );
  }

  if (!creditInfo) {
    return (
      <Card sectioned>
        <Banner status="warning">
          <p>No credit information available.</p>
        </Banner>
      </Card>
    );
  }

  const { credits, subscription, vipSubscription, isVIP } = creditInfo;
  const totalAvailable = (credits.monthly || 0) + (credits.flex || 0);
  const percentUsed = credits.used > 0 && totalAvailable > 0
    ? Math.min(100, Math.round((credits.used / (credits.used + totalAvailable)) * 100))
    : 0;
  
  // Determine which subscription to use for display
  const activeSub = isVIP && vipSubscription ? vipSubscription : subscription;
  
  // Get the reset date for monthly credits
  const resetDate = isVIP ? vipSubscription?.nextCreditResetDate : activeSub?.nextCreditResetDate || activeSub?.nextBillingDate || null;
  
  // Format VIP end date display
  let vipEndDateDisplay = '';
  if (isVIP && vipSubscription?.endDate) {
    vipEndDateDisplay = `until ${new Date(vipSubscription.endDate).toLocaleDateString()}`;
  } else if (isVIP) {
    vipEndDateDisplay = '(Unlimited)';
  }

  return (
    <Card sectioned title="Your Credits">
      <Stack vertical spacing="tight">
        {activeSub ? (
          <Banner status={isVIP ? "attention" : "info"}>
            <Stack vertical spacing="tight">
              <p>
                {isVIP ? (
                  <>
                    <Badge status="attention">VIP</Badge>{' '}
                    You have VIP access to the <strong>{activeSub.planName}</strong> plan {vipEndDateDisplay}.
                  </>
                ) : (
                  <>You are subscribed to the <strong>{activeSub.planName}</strong> plan (${activeSub.price}/month).</>
                )}
              </p>
              {resetDate && (
                <p>
                  <TextStyle variation="subdued">
                    Your monthly credits reset on {new Date(resetDate).toLocaleDateString()}
                  </TextStyle>
                </p>
              )}
            </Stack>
          </Banner>
        ) : (
          <Banner status="warning">
            <p>You don't have an active subscription. Contact admin to set up a subscription plan.</p>
          </Banner>
        )}

        <div style={{ marginTop: '1rem' }}>
          <Stack distribution="equalSpacing" alignment="center">
            <Stack.Item>
              <TextStyle variation="strong">Available Credits: {totalAvailable}</TextStyle>
            </Stack.Item>
            <Stack.Item>
              <TextStyle variation="subdued">Used: {credits.used || 0}</TextStyle>
            </Stack.Item>
          </Stack>
          <div style={{ marginTop: '0.5rem' }}>
            <ProgressBar progress={percentUsed} size="small" />
          </div>
        </div>

        <div style={{ marginTop: '1rem' }}>
          <Stack distribution="equalSpacing">
            <Stack vertical spacing="extraTight">
              <TextStyle variation="strong">Monthly Credits</TextStyle>
              <Badge status={credits.monthly > 0 ? "success" : "attention"}>
                {credits.monthly || 0}
              </Badge>
              {resetDate ? (
                <TextStyle variation="subdued" size="small">
                  Resets on {new Date(resetDate).toLocaleDateString()}
                </TextStyle>
              ) : (
                <TextStyle variation="subdued" size="small">
                  N/A
                </TextStyle>
              )}
            </Stack>
            
            <Stack vertical spacing="extraTight">
              <TextStyle variation="strong">Flex Credits</TextStyle>
              <Badge status={credits.flex > 0 ? "success" : "attention"}>
                {credits.flex || 0}
              </Badge>
              <TextStyle variation="subdued" size="small">
                Never expire
              </TextStyle>
            </Stack>
          </Stack>
        </div>

        <div style={{ marginTop: '1rem' }}>
          <Button onClick={fetchCreditInfo} size="slim">
            Refresh
          </Button>
        </div>
      </Stack>
    </Card>
  );
};

export default UserCreditsInfo;
